import { Container } from '@/atoms';
import {
  CardMostViewed,
  PostHeaderLeft,
  PostHeaderRight,
  Share,
} from '@/molecules';
import { Breadcrumbs } from '@/organisms';
import { FlexibleContent } from '@/templates';

import type { FunctionComponent } from 'react';
import type { TypePostProps } from './types';

/**
 * Post
 */
export const Post: FunctionComponent<TypePostProps> = ({
  author,
  breadcrumbs,
  category,
  cross,
  date,
  flexibleContent,
  image,
  postContent,
  readingTime,
  recommendedPosts,
  title,
  uri,
}: TypePostProps) => (
  <>
    <Container wrapper padding="px-4 py-2.5 lg:px-4 xl:px-0 lg:pt-8 lg:pb-4">
      <Breadcrumbs breadcrumbs={breadcrumbs} mobileIndex={1} />
    </Container>

    <Container
      wrapper
      as="article"
      padding="lg:px-4 xl:p-0"
      classNameWrapper="grid lg:grid-cols-[1fr,440px] lg:grid-rows-[435px,1fr] lg:gap-14"
    >
      <PostHeaderLeft
        date={date}
        image={image}
        category={category}
        title={title}
        readingTime={readingTime}
        author={author}
        className="order-1"
      />

      <div className="order-2 min-w-full px-4 py-4 lg:order-3 lg:p-0">
        <div className="mb-6 flex items-center justify-between gap-3">
          <Share title={title} url={uri} category={category?.label} />
          <span className="u-label u-label--overlline text-typo-tertiary">
            {readingTime && <time>{readingTime} MIN</time>}
          </span>
        </div>

        <FlexibleContent
          type="Post"
          typeFlexible="Cptblog_PostContent"
          flexible={postContent.flexible}
          className="!px-0 md:!py-4"
        />
      </div>

      <div className="order-4 px-4 py-8 lg:p-0">
        {recommendedPosts.title && (
          <h3 className="u-subtitle u-subtitle--m mb-6">
            {recommendedPosts.title}
          </h3>
        )}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-1">
          {recommendedPosts.posts.map((post) => (
            <CardMostViewed
              key={post.title}
              title={post.title}
              image={post.image}
              date={post.date}
              readingTime={post.readingTime}
              href={post.href}
              {...(post.excerpt && {
                excerpt: (
                  <div dangerouslySetInnerHTML={{ __html: post.excerpt }} />
                ),
              })}
            />
          ))}
        </div>
      </div>

      <PostHeaderRight
        image={cross.image}
        title={cross.title}
        description={cross.description}
        link={cross.link}
        className="order-3 lg:order-2"
      />
    </Container>

    <FlexibleContent type="Post" flexible={flexibleContent.flexible} />
  </>
);

Post.displayName = 'Post';
